import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Link, useNavigate } from "react-router-dom"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Mail, Lock } from "lucide-react"
import { useState } from "react"
import Logo from "@/components/layout/logo"
import { usePlatformParams } from "@/hooks/use-platform-params"
import { ROUTES } from "@/config/routes"
import { signin } from "@/api/auth"
import type { LoginCredentials, LoginCodeResponse } from "@/types/auth"
import type { ApiResponse } from "@/types/api"
import { toast } from 'sonner'
 
export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { platformParams, createUrlWithParams } = usePlatformParams();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('이메일을 입력해 주세요.');
      return;
    }
    
    if (!email || !password) {
      toast.error('비밀번호를 입력해주세요.');
      return;
    }
    
    if (!email || !password) {
      toast.error('이메일과 비밀번호를 모두 입력해 주세요.');
      return;
    }

    setIsLoading(true);

    try {
      const credentials: LoginCredentials = {
        email,
        password
      };

      const platform = platformParams.platform || 'cafe24';
      const response: ApiResponse<LoginCodeResponse> = await signin(credentials, platform);
      // API Response가 성공적이면 code가 200이거나 2xx 범위
      if (response.code && response.code >= 200 && response.code < 300) {
        toast.success('로그인이 되었습니다.');
        // redirectUrl로 리다이렉트
        if (response.data.redirectUrl) {
          // 서버에서 받은 redirectUrl에 플랫폼 파라미터가 없다면 추가
          const redirectUrl = new URL(response.data.redirectUrl);
          if (platformParams.platform && !redirectUrl.searchParams.has('platform')) {
            redirectUrl.searchParams.set('platform', platformParams.platform);
          }
          if (platformParams.platform_mall_id && !redirectUrl.searchParams.has('platform_mall_id')) {
            redirectUrl.searchParams.set('platform_mall_id', platformParams.platform_mall_id);
          }
          window.location.href = redirectUrl.toString();
        }
      } else if (response.code === 2004) {
        toast.error(
          <div>
            입력한 이메일 주소로 가입된 정보가 없습니다.<br />
            이메일 주소를 확인해주세요.
          </div>
        );
      } else if (response.code === 2005) {
        toast.error('비밀번호를 확인해 주세요.');
      } else if (response.code === 2018) {
        toast.error('관리자 승인 후 사용이 가능합니다.');
      } else {
        toast.error('이메일 또는 비밀번호를 확인해 주세요.'); 
      }
    } catch (error) {
      console.error('로그인 오류:', error);
      toast.error('로그인 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Logo />
          </div>
          <CardTitle className="text-2xl">임직원 관리 서비스</CardTitle>
          <CardDescription>
            가입하신 계정 정보를 입력해주세요.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">이메일</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  placeholder="이메일을 입력하세요"
                  className="pl-10"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">비밀번호</Label>
                <Link 
                  className="text-sm text-muted-foreground hover:text-foreground underline"
                  to={createUrlWithParams(ROUTES.PASSWORD_FORGOT_EMAIL)}
                >
                  비밀번호를 잊으셨나요?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="비밀번호를 입력하세요"
                  className="pl-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "로그인 중..." : "로그인"}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm text-muted-foreground">
            아직 회원이 아니신가요?{" "}
            <Link 
              className="text-primary hover:text-primary/80 underline"
              to={createUrlWithParams(ROUTES.SIGNUP)}
            >
              회원가입
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}